import { TextField as TextInput } from '@material-ui/core';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useCookies } from 'react-cookie';
import Countdown, { zeroPad } from 'react-countdown';
import { useHistory } from 'react-router-dom';
import API from 'src/api/api';
import { ReactComponent as MobileIcon } from 'src/assets/icons/mobile.svg';
import ErrorMessageWithIcon from 'src/components/ErrorMessageWithIcon/ErrorMessageWithIcon.component';
import ErrorPopup from 'src/components/ErrorPopup/ErrorPopup';
import LoadingContainer from 'src/components/LoadingContainer/LoadingContainer';
import PrimaryButton from 'src/components/PrimaryButton/PrimaryButton.component';
import { APP_CONSTANT } from 'src/constants';
import { t } from 'src/i18n';
import {
  deleteAllCookie,
  getCookie,
  parseJSON,
  setCookie
} from 'src/utils/common.util';
import * as Yup from 'yup';
import Routes from '../../Navigator/Navigator.route';
import { LinkWrapper, useStyles } from '../Login.style';
import ResendOtpButton from './ResendOtpButton.component';
import { pathOr } from 'ramda';
import { AuthUtil } from 'src/utils';
import { KEYCODE_MAPPING, isNumericKey, isSpecialKey } from './constants';
import { getUserInfo } from '../utils';

interface IProps {
  verificationMethod: string;
}

const LoginOTP: React.FC<IProps> = (props) => {
  const [showCountdown, setShowCountDown] = useState<boolean>(true);
  const [resendErrorMessage, setResendErrorMessage] = React.useState<string>(
    ''
  );
  const [otpErrorMessage, setOtpErrorMessage] = React.useState<string>('');
  const [userInfoErrorMessage, setUserInfoErrorMessage] = React.useState<
    string
  >('');
  const [showErrorPopup, setShowErrorPopup] = React.useState<boolean>(false);
  const [expiredTime, setExpiredTime] = React.useState<string>('');
  const [isExpired, setIsExpired] = React.useState<boolean>(false);
  const [countdownTimestamp, setCountdownTimestamp] = React.useState<number>(0);
  const [otpFormatError, setOtpFormatError] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const classes = useStyles();
  const [cookie, setCookieFn] = useCookies([]);

  const { verificationMethod } = props;

  const resetErrorMessage = (err) => {
    setResendErrorMessage('');
    setOtpErrorMessage('');
    setUserInfoErrorMessage(err.response.data.message);
    setShowErrorPopup(true);
    deleteAllCookie();
  };

  const handleSubmitOTP = async (value) => {
    const authId = getCookie('authId');

    try {
      const response = await API().post('/auth/v1/public/oauth/token', {
        authId,
        otp: value.otpCode,
        grantType: 'otp'
      });

      if (response.status !== 200) {
        return;
      }

      setCookie('token', response.data.token, { duration: 24 });
      setCookie('contactId', response.data.contactId, { duration: 24 });
      await getUserInfo(response, setCookieFn, resetErrorMessage, history);
      setIsLoading(false);
    } catch (err) {
      const errorMessage = pathOr(err.message, ['response', 'data', 'message'])(
        err
      );
      setOtpErrorMessage(errorMessage);
      setResendErrorMessage('');
      setUserInfoErrorMessage('');
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  };

  const history = useHistory();
  const {
    handleSubmit,
    values,
    handleChange,
    handleBlur,
    touched,
    errors
  } = useFormik({
    initialValues: {
      otpCode: ''
    },

    onSubmit: async (val) => {
      setIsLoading(true);
      await handleSubmitOTP(val);
    },

    validationSchema: Yup.object().shape({
      otpCode: Yup.number()
        .required(t('signIn.otp.authenCodeRequired'))
        .typeError(t('signIn.otp.format'))
    })
  });

  const handleOTPChange = (e) => {
    const reg = new RegExp('^[0-9]$');
    if (e.target.value.length > 6) {
      return;
    }
    e.target.value = e.target.value.replace(/\D/g, '');
    handleChange(e);
  };

  const handleResendOtpCode = () => {
    setIsExpired(false);
    setIsLoading(true);
    const authId = getCookie('authId');
    const accountId = (parseJSON('tenantInfo') as any).accountId;

    API()
      .post('/auth/v1/public/send-otp', {
        authId,
        accountId,
        type: props.verificationMethod
      })
      .then((response) => {
        if (response.status !== 200) {
          return;
        }

        setExpiredTime(response.data.remainingTime);

        setCookie('expiredDateTime', response.data.expiredDateTime, {
          duration: 24
        });
        setCookie('remainingTime', response.data.remainingTime, {
          duration: 24
        });

        if (!showCountdown) {
          setShowCountDown(true);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        const errorMessage = pathOr(err.message, [
          'response',
          'data',
          'message'
        ])(err);
        setResendErrorMessage(errorMessage);

        setOtpErrorMessage('');
        setUserInfoErrorMessage('');
        setShowErrorPopup(true);
        setIsLoading(false);
      });
  };

  const directLocation = (pathName) => {
    return pathName || '';
  };

  const countdownRenderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      // Render a completed state

      return '';
    }

    return (
      <span>
        {zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const countDownOnComplete = () => {
    setIsExpired(true);
    setExpiredTime('0');
  };

  const handleKeydown = (event) => {
    const charCode = event.which ? event.which : event.keyCode;

    const isNumberAndSpecialKey =
      isNumericKey(charCode) || isSpecialKey(charCode);

    // allow user to use Ctrl (Command in Mac) + V, Ctrl +A, Ctrl + X
    if (
      (event.ctrlKey || event.metaKey) &&
      [KEYCODE_MAPPING.v, KEYCODE_MAPPING.a, KEYCODE_MAPPING.x].includes(
        event.keyCode
      )
    ) {
      return true;
    }

    if (!isNumberAndSpecialKey) {
      event.preventDefault();
    } else if (charCode === KEYCODE_MAPPING.enter) {
      handleSubmit();
    }

    return true;
  };

  React.useEffect(() => {
    const remainingTime = getCookie('remainingTime');

    if (remainingTime) {
      setExpiredTime(remainingTime);
    }
  }, []);

  React.useEffect(() => {
    setCountdownTimestamp(Date.now() + Number(expiredTime) * 1000);
  }, [expiredTime]);

  React.useEffect(() => {
    if (!!errors.otpCode && !!touched.otpCode) {
      setOtpFormatError(errors.otpCode);
    } else {
      setOtpFormatError('');
    }
  }, [errors, touched]);

  return (
    <LoadingContainer isLoading={isLoading} isOverlay={true}>
      <>
        <p className="login__otp-title">{t('signIn.otp.title')}</p>

        <form autoComplete="off">
          <div className="login__input-group">
            <div style={{ display: 'none' }}>
              <input
                type="text"
                id="PreventChromeAutocomplete"
                name="PreventChromeAutocomplete"
                autoComplete="address-level4"
              />
            </div>

            <TextInput
              className={classes.input}
              onChange={handleOTPChange}
              name="otpCode"
              value={values.otpCode}
              type="text"
              label={null}
              autoComplete="off"
              variant="outlined"
              placeholder={t('signIn.otp.placeHolder')}
              onBlur={handleBlur}
              error={!!errors.otpCode && !!touched.otpCode}
              inputProps={{ style: { fontSize: 20 }, maxLength: 6 }}
              onKeyDown={handleKeydown}
            />
            {otpFormatError && (
              <ErrorMessageWithIcon message={otpFormatError} />
            )}
          </div>
        </form>

        <LinkWrapper to={directLocation('')}>
          {t('signIn.otp.whatIsIt')}
        </LinkWrapper>

        <ErrorPopup
          showErrorPopup={showErrorPopup}
          setShowErrorPopup={setShowErrorPopup}
          errorMessage={
            otpErrorMessage || resendErrorMessage || userInfoErrorMessage
          }
        />

        <PrimaryButton
          onClick={handleSubmit as any}
          title={t('signIn.otp.button.verify')}
          variant="contained"
          titleClassName="login"
        />
        <div className="login__resend-otp">
          <div className="login__mobile-icon">
            {verificationMethod === APP_CONSTANT.EMAIL ? (
              <MailOutlineIcon />
            ) : (
              <MobileIcon />
            )}
          </div>
          <div>
            <p className="login__resend-description">
              {verificationMethod === APP_CONSTANT.EMAIL
                ? t('signIn.otp.resend.descriptionEmail')
                : t('signIn.otp.resend.descriptionSMS')}
            </p>

            <ResendOtpButton
              handleResendOtpCode={handleResendOtpCode}
              isDisabled={!isExpired}
            />
            <span
              style={{
                color: '#4C7CFD',
                fontFamily: 'TTCommons',
                fontWeight: 'bold'
              }}
            >
              {showCountdown && !resendErrorMessage && (
                <Countdown
                  date={countdownTimestamp}
                  key={countdownTimestamp}
                  renderer={countdownRenderer}
                  onComplete={countDownOnComplete}
                />
              )}
            </span>
          </div>
        </div>
      </>
    </LoadingContainer>
  );
};

export default LoginOTP;
